/**
 * Screen 1 — take the photographs and choose who does the talking.
 *
 * `<input type="file" capture="environment" multiple>` opens the camera on both
 * iOS and Android without a native app. Downscale, upload straight to GCS with
 * the signed URLs, then start the scan; nothing but JSON goes through Cloud Run.
 */
import { useQuery } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { api, uploadSigned } from '../lib/api'
import { downscale } from '../lib/downscale'
import { useSession } from '../lib/session'

interface Shot {
  id: string
  file: File
  preview: string
  state: 'pending' | 'uploading' | 'done' | 'failed'
}

const DEPTH_BLURB: Record<string, string> = {
  fast: 'One pass per bottle. Thirty seconds, broad strokes.',
  balanced: 'Six specialists per bottle. The house default.',
  deep: 'Everything, twice, with sources. Slow and thorough.',
}

const MODES = [
  {
    id: 'cellar',
    name: 'My cellar',
    blurb: 'Bottles you own. Every one photographed goes into your cellar.',
  },
  {
    id: 'shopping',
    name: 'Out shopping',
    blurb: 'A shop shelf. Nothing is added to your cellar — you have not bought it yet.',
  },
] as const

export default function Capture() {
  const navigate = useNavigate()
  const { config } = useSession()
  const input = useRef<HTMLInputElement>(null)

  const [shots, setShots] = useState<Shot[]>([])
  const [personaId, setPersonaId] = useState<string | null>(null)
  const [depth, setDepth] = useState(config.defaultResearchDepth)
  const [mode, setMode] = useState<'cellar' | 'shopping'>('cellar')
  const [context, setContext] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const shopping = mode === 'shopping'

  const personas = useQuery({ queryKey: ['personas'], queryFn: api.personas })
  const chosenPersona = personaId ?? personas.data?.defaultId ?? null

  function addFiles(files: FileList | null) {
    if (!files) return
    const room = config.maxImages - shots.length
    const accepted = [...files].slice(0, Math.max(0, room))
    if (files.length > accepted.length) {
      setError(`${config.maxImages} photographs at a time is the limit.`)
    }
    setShots((current) => [
      ...current,
      ...accepted.map((file, index) => ({
        id: `img${current.length + index}`,
        file,
        preview: URL.createObjectURL(file),
        state: 'pending' as const,
      })),
    ])
  }

  function remove(id: string) {
    setShots((current) => {
      const going = current.find((shot) => shot.id === id)
      if (going) URL.revokeObjectURL(going.preview)
      // Re-index, because the ids are positional and the server pairs the
      // signed URLs to them by name.
      return current
        .filter((shot) => shot.id !== id)
        .map((shot, index) => ({ ...shot, id: `img${index}` }))
    })
  }

  async function go() {
    if (!shots.length || !chosenPersona) return
    setError(null)
    setBusy('Shrinking the photographs…')

    try {
      const prepared = await Promise.all(shots.map(async (shot) => ({ shot, blobs: await downscale(shot.file) })))

      setBusy('Creating the scan…')
      const scan = await api.createScan({
        images: prepared.map(({ shot, blobs }) => ({
          imageId: shot.id,
          contentType: 'image/jpeg',
          seenAt: blobs.seenAt,
        })),
        personaId: chosenPersona,
        researchDepth: depth,
        mode,
        context: shopping ? context.trim() : '',
      })

      setBusy('Uploading…')
      const uploaded: string[] = []
      await Promise.all(
        scan.uploads.map(async (upload) => {
          const match = prepared.find(({ shot }) => shot.id === upload.imageId)
          if (!match) return
          try {
            // Both variants at once: the analysis frame is what the agent
            // reads, the original is what the sighting timeline shows.
            await Promise.all([
              uploadSigned(upload.analysis, match.blobs.analysis),
              uploadSigned(upload.original, match.blobs.original),
            ])
            uploaded.push(upload.imageId)
          } catch (uploadError) {
            // One failed photograph should not cost the other five.
            console.warn('upload', upload.imageId, uploadError)
          }
        }),
      )

      if (!uploaded.length) throw new Error('None of the photographs would upload.')

      setBusy('Waking the sommelier…')
      await api.startScan(scan.runId, uploaded)
      navigate(`/scan/${scan.runId}`)
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure))
      setBusy(null)
    }
  }

  return (
    <div className="space-y-6 pt-5">
      <section>
        <h2 className="font-display text-2xl">
          {shopping ? 'What should you buy?' : 'What have you got?'}
        </h2>
        <p className="mt-1 text-sm text-stone-400">
          Up to {config.maxImages} photographs. A whole shelf in one frame is fine — every
          bottle gets its own researcher.
        </p>
      </section>

      <section className="space-y-2">
        <h3 className="label">Where are you?</h3>
        <div className="flex flex-wrap gap-2">
          {MODES.map((option) => (
            <button
              key={option.id}
              onClick={() => setMode(option.id)}
              disabled={Boolean(busy)}
              className={`chip ${mode === option.id ? 'chip-on' : ''}`}
            >
              {option.name}
            </button>
          ))}
        </div>
        <p className="text-xs text-stone-500">
          {MODES.find((option) => option.id === mode)?.blurb}
        </p>
      </section>

      <div className="grid grid-cols-3 gap-2">
        {shots.map((shot) => (
          <figure key={shot.id} className="relative aspect-3/4 overflow-hidden rounded-xl">
            <img src={shot.preview} alt="" className="h-full w-full object-cover" />
            <button
              onClick={() => remove(shot.id)}
              disabled={Boolean(busy)}
              aria-label="Remove this photograph"
              className="absolute top-1.5 right-1.5 grid h-7 w-7 place-items-center rounded-full bg-black/70 text-sm"
            >
              ×
            </button>
          </figure>
        ))}

        {shots.length < config.maxImages && (
          <button
            onClick={() => input.current?.click()}
            disabled={Boolean(busy)}
            className="grid aspect-3/4 place-items-center rounded-xl border border-dashed border-white/15 text-3xl text-stone-500 hover:border-wine-500/60 hover:text-wine-300"
          >
            +
          </button>
        )}
      </div>

      <input
        ref={input}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="hidden"
        onChange={(event) => {
          addFiles(event.target.files)
          event.target.value = ''
        }}
      />

      <section className="space-y-2">
        <h3 className="label">Who should judge them?</h3>
        <div className="flex flex-wrap gap-2">
          {(personas.data?.personas ?? []).map((persona) => (
            <button
              key={persona.id}
              onClick={() => setPersonaId(persona.id)}
              className={`chip ${chosenPersona === persona.id ? 'chip-on' : ''}`}
              title={persona.tagline}
            >
              {persona.name}
            </button>
          ))}
        </div>
        {chosenPersona && (
          <p className="pt-1 text-xs text-stone-500 italic">
            {personas.data?.personas.find((p) => p.id === chosenPersona)?.sampleLine}
          </p>
        )}
      </section>

      {/* No depth picker when shopping: the server forces `fast` there, because
          grounding costs minutes you do not have standing in an aisle. */}
      {shopping ? (
        <section className="space-y-2">
          <h3 className="label">What is it for?</h3>
          <textarea
            value={context}
            onChange={(event) => setContext(event.target.value.slice(0, config.maxContextChars))}
            disabled={Boolean(busy)}
            rows={3}
            placeholder="Steak on Friday, or a present for someone who likes Burgundy, or nothing much under fifteen pounds."
            className="w-full resize-none rounded-xl border border-white/10 bg-black/20 p-3 text-sm text-stone-200 placeholder:text-stone-600 focus:border-wine-500/60 focus:outline-none"
          />
          <p className="text-xs text-stone-500">
            Optional. It moves the ranking — a gift leans on quality, a budget leans on value —
            and it is the question the critics answer.{' '}
            {config.maxContextChars - context.length} characters left.
          </p>
        </section>
      ) : (
        <section className="space-y-2">
          <h3 className="label">How hard should they look?</h3>
          <div className="flex flex-wrap gap-2">
            {config.researchDepths.map((option) => (
              <button
                key={option}
                onClick={() => setDepth(option)}
                className={`chip ${depth === option ? 'chip-on' : ''}`}
              >
                {option}
              </button>
            ))}
          </div>
          <p className="text-xs text-stone-500">{DEPTH_BLURB[depth] ?? ''}</p>
        </section>
      )}

      {error && <p className="text-sm text-rose-300">{error}</p>}

      <button
        className="btn-primary w-full"
        disabled={!shots.length || Boolean(busy) || !chosenPersona}
        onClick={() => void go()}
      >
        {busy ?? `Rank ${shots.length || ''} ${shots.length === 1 ? 'photograph' : 'photographs'}`.trim()}
      </button>
    </div>
  )
}
